import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { FormattedMessage } from 'react-intl'
import { get } from 'lodash'
import { compose } from 'recompose'

import { wizard, meta } from 'helpers/decorators'
import { ConfigurationFile, ConfigurationWrapper } from 'containers/misc'
import { Account as AccountForm } from 'components/forms'
import { Checkbox } from 'components/fields'
import { Button, FormDebug } from 'components/misc'
import { apiLeaders, apiConfiguration } from 'helpers/decorators/api'
import { CONFIGURATION_KEYS, HIERARCHY_LEVELS } from 'constants/enums'
import validate from './account.validation'
import styles from './account.styl'

const enhance = compose(
  apiLeaders(),
  apiConfiguration(),
  wizard({
    form: 'leaderRegistration',
    multipage: true,
    validate,
    destroyOnUnmount: false
  }),
  meta({
    title: 'Account Details',
    intlKey: 'wizard.orb_account'
  })
)

class AccountDetails extends Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    createLeader: PropTypes.func.isRequired,
    onGetConfigurationValue: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    leaders: PropTypes.object,
    configuration: PropTypes.object,
    values: PropTypes.object
  }

  componentDidMount = async() => {
    const { values } = this.props

    const volunteerCodeOfConductCfg = await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.volunteerCodeOfConduct.id, get(values, 'organizationId'), true)
    const isVolunteerCodeOfConductEnabled = get(volunteerCodeOfConductCfg, 'body.booleanValue', false)

    if (isVolunteerCodeOfConductEnabled) {
      await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.auCoConductDoc.id, get(values, 'organizationId'), true)
    }

    const naoRegRequirementCfg = await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.enableNaoRegRequirements.id, get(values, 'organizationId'), true)
    const isNaoRegRequirementEnabled = get(naoRegRequirementCfg, 'body.booleanValue', false)

    if (isNaoRegRequirementEnabled) {
      await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.naoRegRequirementsUrl.id, get(values, 'organizationId'), true)
    }

    const privacyPolicyAgreementCfg = await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.enablePrivacyPolicyAgreement.id, get(values, 'organizationId'), true)
    const isPrivacyPolicyAgreementEnabled = get(privacyPolicyAgreementCfg, 'body.booleanValue', false)

    if (isPrivacyPolicyAgreementEnabled) {
      await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.privacyPolicyUrl.id, get(values, 'organizationId'), true)
    }
  }

  handleSubmit = fields => {
    this.props
      .createLeader(fields)
      .then(() => {
        this.props.onSubmit()
        this.props.reset()
      })
      .catch(err => console.debug(err))
  }

  render() {
    const { handleSubmit, values, leaders, configuration } = this.props
    const hierarchyLevel = get(values, 'hierarchyLevel.value')
    const orgId = get(values, 'organizationId')

    const hasConductEnabled = get(configuration, `values[${CONFIGURATION_KEYS.volunteerCodeOfConduct.id}].booleanValue`)
    const conductUrl = get(configuration, `values[${CONFIGURATION_KEYS.auCoConductDoc.id}].fileUrl`)

    const isNaoRegRequirementEnabled = get(configuration, `values[${CONFIGURATION_KEYS.enableNaoRegRequirements.id}].booleanValue`)
    const naoRegRequirementUrl = get(configuration, `values[${CONFIGURATION_KEYS.naoRegRequirementsUrl.id}].textValue`)

    const isPrivacyPolicyAgreementEnabled = get(configuration, `values[${CONFIGURATION_KEYS.enablePrivacyPolicyAgreement.id}].booleanValue`)
    const privacyPolicyUrl = get(configuration, `values[${CONFIGURATION_KEYS.privacyPolicyUrl.id}].textValue`)

    return (
      <form onSubmit={handleSubmit(this.handleSubmit)}>
        <AccountForm values={values} />
        <FormDebug values={values} />

        <div className="row">
          <div className="col-xs-12">

            <div style={{ float: 'left' }}>
            <Checkbox
              name="helpers.terms"
              label="I have read the <a href='{url}' target='_blank'>Terms and conditions and agree with them."
              intlKey="label.i_have_read_the_terms_and_conditions"
              intlValues={{
                url: '/orb_parental_consent_form.pdf'
              }}
            />
            </div>
            <div style={{ float: 'left', marginTop: 1, marginLeft: 5 }}>
            <ConfigurationFile
              configKey={CONFIGURATION_KEYS.termsAndConditionsLeader.id}
              organizationId={orgId}
              isPublic
              formattedName={
                <FormattedMessage
                  id="button.terms_and_conditions"
                  defaultMessage="International Terms & Conditions"
                />
              }
            />
            </div>
            <div style={{ clear: 'both' }}></div>
            <div className={styles.agreementsWrapper}>
            {hasConductEnabled &&
              conductUrl &&
              <Checkbox
                name="helpers.agreementCodeOfConduct"
                label="I Agree to Volunteer Code of Conduct."
                intlKey="label.i_agree_to_volunteer_code"
                intlValues={{
                  url: conductUrl
                }}
              />}

            {hierarchyLevel !== HIERARCHY_LEVELS.IAC.id &&
              isNaoRegRequirementEnabled &&
              naoRegRequirementUrl &&
            <Checkbox
              name="helpers.naa"
              label="I confirm that I meet all NAA Regulation Requirements. <a href='{url}' target='_blank'>For more information click here.</a>"
              intlKey="label.i_confirm_that_i_meet"
              intlValues={{
                url: naoRegRequirementUrl
              }}
            />}

            {isPrivacyPolicyAgreementEnabled &&
            privacyPolicyUrl &&
            <Checkbox
              name="helpers.agreementPrivacyPolicy"
              label="I have read the Privacy Policy and agree with. <a href='{url}' target='_blank'>The Privacy Policy is here.</a>"
              intlKey="label.i_agree_to_privacy_policy"
              intlValues={{
                url: privacyPolicyUrl
              }}
            />}

            <ConfigurationWrapper
              configKey={CONFIGURATION_KEYS.marketingMaterialsFromNao.id}
              type="booleanValue"
              orgId={orgId}
            >
              <Checkbox
                name="helpers.marketingFromNao"
                label="Opt in to receive marketing materials from the NAO."
                intlKey="label.marketing_from_nao"
              />
            </ConfigurationWrapper>
            <ConfigurationWrapper
              configKey={CONFIGURATION_KEYS.marketingMaterialsFromOa.id}
              type="booleanValue"
              orgId={orgId}
            >
              <Checkbox
                name="helpers.marketingFromOa"
                label="Opt in to receive marketing materials from the Operating Authority."
                intlKey="label.marketing_from_oa"
              />
            </ConfigurationWrapper>
            <ConfigurationWrapper
              configKey={CONFIGURATION_KEYS.marketingMaterialsFromIaf.id}
              type="booleanValue"
              orgId={orgId}
            >
              <Checkbox
                name="helpers.marketingFromIaf"
                label="Opt in to receive marketing materials from the International Foundation."
                intlKey="label.marketing_from_iaf"
              />
            </ConfigurationWrapper>
          </div>
        </div>
        </div>

        <Button type="submit" disabled={get(leaders, 'loading')}>
          <FormattedMessage id="button.finish" defaultMessage="Finish" />
        </Button>
      </form>
    )
  }
}

export default enhance(AccountDetails)
